<script lang="ts">
	/**
	 * The seat delegation editor for a parent licence.
	 *
	 * The model it presents is the one the licence actually has: a fixed pool of
	 * seats, some kept for your own machines and some given away in blocks. So the
	 * budget is stated once at the top and every edit is measured against it,
	 * rather than leaving the arithmetic to the person adjusting the numbers.
	 *
	 * Adding someone and adjusting an existing block are deliberately different
	 * actions. An editable email beside each block would make "give Ada's seats to
	 * Grace" a single keystroke away from "rename Ada" — and the backend treats a
	 * changed address as a different person, so the seats would move silently.
	 * Identity is fixed once assigned; to move seats you remove the block and add
	 * another.
	 *
	 * Nothing here is authoritative: the backend re-checks the budget and the
	 * caller's right to delegate on every save.
	 */

	import { Button, Input, Spinner, Textarea } from 'flowbite-svelte';
	import EmptyState from './EmptyState.svelte';
	import type { License, SeatAssignmentInput } from '../AppTypes';
	import { t, t2 } from '../i18n';

	interface Props {
		/** The licence whose seats are being delegated. */
		license: License;
		/** Whether a save is in flight. */
		saving?: boolean;
		/** Called with the complete assignment list when the user saves. */
		onsave: (_assignments: SeatAssignmentInput[]) => void;
	}

	let { license, saving = false, onsave }: Props = $props();

	/** One block of seats as the user is editing it. */
	interface Draft extends SeatAssignmentInput {
		/** Display name of the assignee, when known. */
		name: string | null;
		/** Seats of this block currently taken by the assignee's machines. */
		used: number;
		/** `true` for a block added in this session and not yet saved. */
		isNew: boolean;
	}

	/** The assignments as the user is editing them. */
	let drafts = $state<Draft[]>([]);
	/** Email typed into the composer. */
	let newEmail = $state('');
	/** Seat count typed into the composer. */
	let newSeats = $state(1);
	/** Validation message for the composer. */
	let composerError = $state('');
	/** What a batch add actually did, when some entries were passed over. */
	let composerNotice = $state('');
	/** Whether the composer is taking a list rather than one address. */
	let isBulk = $state(false);
	/** Addresses pasted into the batch field, separated however the caller likes. */
	let bulkEmails = $state('');
	/** Validation message for the save action. */
	let saveError = $state('');

	/** Rebuilds the draft list from the saved licence. */
	export function reset(): void {
		composerError = '';
		composerNotice = '';
		saveError = '';
		newEmail = '';
		newSeats = 1;
		bulkEmails = '';
		drafts = license.assignments.map((assignment) => ({
			licenseUUID: assignment.licenseUUID,
			userUUID: assignment.assignee.userUUID,
			userEmail: assignment.assignee.email ?? '',
			seats: assignment.seats,
			name: assignment.assignee.name,
			used: assignment.used,
			isNew: false
		}));
	}

	/* Rebuild whenever the licence is replaced — a save returns a fresh one. */
	$effect(() => {
		license;
		reset();
	});

	/** Seats currently promised to other people. */
	let assigned = $derived(drafts.reduce((sum, draft) => sum + (Number(draft.seats) || 0), 0));

	/** Seats left for the licence holder's own machines. Negative means over budget. */
	let remaining = $derived(license.seats.total - assigned);

	/** Percentage of the pool promised away, for the budget bar. */
	let assignedPercent = $derived(
		license.seats.total > 0 ? Math.min(100, Math.round((assigned / license.seats.total) * 100)) : 0
	);

	/** Whether the draft differs from what is saved. */
	let isDirty = $derived(
		JSON.stringify(drafts.map((d) => [d.licenseUUID, d.userEmail, d.seats])) !==
			JSON.stringify(license.assignments.map((a) => [a.licenseUUID, a.assignee.email ?? '', a.seats]))
	);

	/** Minimal address check. The backend validates properly; this catches typos early. */
	function isEmail(value: string): boolean {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
	}

	/** Initials for the avatar, from a name if there is one and the address otherwise. */
	function initials(draft: Draft): string {
		const source = draft.name?.trim() || draft.userEmail;
		const parts = source.split(/[\s.@_-]+/).filter(Boolean);
		return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
	}

	/** Adds the composer's contents as a new block. */
	function add(): void {
		composerError = '';
		composerNotice = '';
		const email = newEmail.trim().toLowerCase();

		/* Catch the typo here rather than after a round trip; the backend validates too. */
		if (!isEmail(email)) {
			composerError = t2('Enter a valid email address.');
			return;
		}
		/* One block per person: two blocks for the same address would race each
		   other on the backend, which keys a delegation by its owner. */
		if (drafts.some((draft) => draft.userEmail.trim().toLowerCase() === email)) {
			composerError = t2('That person already has seats on this license.');
			return;
		}
		const seats = Number(newSeats);
		/* A block of zero seats is not an assignment, it is a mistake. */
		if (!Number.isInteger(seats) || seats < 1) {
			composerError = t2('Assign at least one seat.');
			return;
		}
		/* Refuse to promise seats the licence does not have; the bar above already
		   shows what is left, so this only ever fires on a deliberate overshoot. */
		if (seats > remaining) {
			composerError = t2('Only {{count}} seat(s) left to assign.', { count: Math.max(0, remaining) });
			return;
		}

		drafts = [
			...drafts,
			{ licenseUUID: null, userUUID: null, userEmail: email, seats, name: null, used: 0, isNew: true }
		];
		newEmail = '';
		newSeats = 1;
	}

	/**
	 * Adds every address in the batch field, at {@link newSeats} seats each.
	 *
	 * Addresses may be separated by commas, semicolons, newlines or whitespace —
	 * people paste from spreadsheets, mail clients and chat, and none of them agree
	 * on a separator. Duplicates within the paste and people who already hold seats
	 * are passed over rather than rejected: pasting a team list that happens to
	 * include two colleagues who are already set up is normal, not a mistake.
	 *
	 * The whole batch is refused when it would not fit, rather than adding as many
	 * as happen to fit — a partial result from a paste is the kind of thing nobody
	 * notices until a colleague says they never got access.
	 */
	function addMany(): void {
		composerError = '';
		composerNotice = '';

		const seats = Number(newSeats);
		/* Same floor as a single add: a block of zero seats is not an assignment. */
		if (!Number.isInteger(seats) || seats < 1) {
			composerError = t2('Assign at least one seat.');
			return;
		}

		const entries = bulkEmails
			.split(/[\s,;]+/)
			.map((entry) => entry.trim().toLowerCase())
			.filter(Boolean);

		/* An empty paste is a slip, not a request. */
		if (entries.length === 0) {
			composerError = t2('Paste at least one email address.');
			return;
		}

		/* Plain objects rather than `Set`: these are local to one call, not reactive
		   state, and a `Set` here trips the rule guarding against un-reactive
		   collections held in `$state`. */
		const existing: Record<string, true> = {};
		/* Index who already holds seats, so the scan below is a lookup per address. */
		for (const draft of drafts) existing[draft.userEmail.trim().toLowerCase()] = true;
		const seen: Record<string, true> = {};
		const invalid: string[] = [];
		const alreadyAssigned: string[] = [];
		const additions: string[] = [];

		/* Sort each pasted address into one of three buckets: add it, skip it because
		   they already hold seats, or skip it because it is not an address. */
		for (const entry of entries) {
			/* Silently collapse a repeated paste; it is not worth a complaint. */
			if (seen[entry]) continue;
			seen[entry] = true;

			if (!isEmail(entry)) {
				invalid.push(entry);
				continue;
			}
			if (existing[entry]) {
				alreadyAssigned.push(entry);
				continue;
			}
			additions.push(entry);
		}

		/* Nothing left to add — say which of the two reasons it was. */
		if (additions.length === 0) {
			composerError =
				invalid.length > 0 && alreadyAssigned.length === 0
					? t2('None of those look like email addresses.')
					: t2('Everyone on that list already has seats on this license.');
			return;
		}

		const needed = additions.length * seats;
		/* All or nothing: see above. */
		if (needed > remaining) {
			composerError = t2('{{needed}} seat(s) needed for {{people}} people, but only {{available}} left.', {
				needed,
				people: additions.length,
				available: Math.max(0, remaining)
			});
			return;
		}

		drafts = [
			...drafts,
			...additions.map((userEmail) => ({
				licenseUUID: null,
				userUUID: null,
				userEmail,
				seats,
				name: null,
				used: 0,
				isNew: true
			}))
		];
		bulkEmails = '';

		/* Say what was passed over. Staying silent about it is how someone ends up
		   believing a colleague was added when they were skipped as a duplicate. */
		const passedOver: string[] = [];
		/* Name each reason separately; "skipped 3" leaves the reader guessing. */
		if (alreadyAssigned.length > 0) {
			passedOver.push(t2('{{count}} already assigned', { count: alreadyAssigned.length }));
		}
		/* Likewise for addresses that were never usable. */
		if (invalid.length > 0) {
			passedOver.push(t2('{{count}} not a valid address', { count: invalid.length }));
		}
		composerNotice =
			passedOver.length > 0
				? t2('Added {{count}}. Skipped: {{skipped}}.', {
						count: additions.length,
						skipped: passedOver.join(', ')
					})
				: t2('Added {{count}}.', { count: additions.length });
	}

	/** Changes one block's seat count, refusing to drop it below what is already in use. */
	function setSeats(index: number, seats: number): void {
		saveError = '';
		const draft = drafts[index];
		/* Seats already held by machines cannot be taken back by editing a number —
		   the machines have to be released first, which is the Machines panel's job. */
		const floor = Math.max(1, draft.used);
		drafts[index] = { ...draft, seats: Math.max(floor, seats) };
	}

	/** Removes a block. Saving is what actually withdraws the seats. */
	function remove(index: number): void {
		saveError = '';
		drafts = drafts.filter((_, i) => i !== index);
	}

	/** Hands the complete list to the caller. */
	function save(): void {
		saveError = '';
		/* The stepper cannot produce this, but a blocked composer entry plus a manual
		   seat count can — so it is checked once more before anything is sent. */
		if (remaining < 0) {
			saveError = t2('Assigned seats ({{assigned}}) exceed the license total ({{total}}).', {
				assigned,
				total: license.seats.total
			});
			return;
		}
		onsave(
			drafts.map((draft) => ({
				licenseUUID: draft.licenseUUID,
				userUUID: draft.userUUID,
				userEmail: draft.userEmail.trim(),
				seats: Number(draft.seats)
			}))
		);
	}
</script>

<!-- Budget. Stated before the list because every edit below is measured against it. -->
<div class="bg-depth rounded-xl p-4">
	<div class="flex flex-wrap items-baseline justify-between gap-2">
		<span class="card-label">{$t('Seat budget')}</span>
		<span class="text-sm text-gray-500 dark:text-gray-400">
			{$t('{{assigned}} of {{total}} assigned', { assigned, total: license.seats.total })}
		</span>
	</div>

	<div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
		<div
			class="h-full rounded-full transition-all {remaining < 0 ? 'bg-red-500' : 'bg-primary-500'}"
			style="width: {assignedPercent}%"
		></div>
	</div>

	<p class="mt-2 text-xs {remaining < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}">
		{#if remaining < 0}
			{$t('{{count}} seat(s) over the license total.', { count: -remaining })}
		{:else}
			{$t('{{count}} seat(s) left for your own machines.', { count: remaining })}
		{/if}
	</p>
</div>

<!-- Assigned blocks. -->
<div class="mt-4 space-y-2">
	{#each drafts as draft, index (draft.licenseUUID ?? draft.userEmail)}
		<div class="widget flex flex-wrap items-center gap-3 !p-3">
			<div
				class="bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
			>
				{initials(draft)}
			</div>

			<div class="min-w-0 flex-1">
				<p class="truncate text-sm font-medium text-gray-900 dark:text-white">
					{draft.name || draft.userEmail}
				</p>
				<p class="truncate text-xs text-gray-500 dark:text-gray-400">
					{#if draft.name}{draft.userEmail} ·
					{/if}
					{#if draft.isNew}
						{$t('Not yet saved')}
					{:else}
						{$t('{{used}} of {{seats}} in use', { used: draft.used, seats: draft.seats })}
					{/if}
				</p>
			</div>

			<!-- A stepper rather than a free number field: seats move one at a time,
			     and the lower bound is what the assignee's machines already hold. -->
			<div class="flex items-center gap-1">
				<Button
					size="xs"
					color="alternative"
					class="!p-2"
					disabled={draft.seats <= Math.max(1, draft.used)}
					onclick={() => setSeats(index, Number(draft.seats) - 1)}
					aria-label={$t('Remove a seat')}
				>
					<i class="fa-solid fa-minus text-[10px]"></i>
				</Button>
				<span class="w-8 text-center text-sm font-semibold text-gray-900 dark:text-white">{draft.seats}</span>
				<Button
					size="xs"
					color="alternative"
					class="!p-2"
					disabled={remaining <= 0}
					onclick={() => setSeats(index, Number(draft.seats) + 1)}
					aria-label={$t('Add a seat')}
				>
					<i class="fa-solid fa-plus text-[10px]"></i>
				</Button>
			</div>

			<Button
				size="xs"
				color="alternative"
				class="!p-2"
				onclick={() => remove(index)}
				aria-label={$t('Remove assignment')}
			>
				<i class="fa-solid fa-xmark text-xs"></i>
			</Button>
		</div>
	{:else}
		<EmptyState
			faIcon="user-group"
			title={$t('No seats assigned')}
			description={$t('Every seat on this license is yours to use. Assign a block below to share it.')}
			compact
		/>
	{/each}
</div>

<!-- Composer. Deliberately distinct from the rows above: adding a person and
     adjusting their seats are different actions with different consequences. -->
<div class="bg-depth mt-4 rounded-xl p-3">
	<div class="flex flex-wrap items-baseline justify-between gap-2">
		<span class="card-label">{$t('Assign seats to a colleague')}</span>
		<!-- One seat count serves both modes, so switching is a change of input, not
		     of intent. -->
		<button
			type="button"
			class="text-primary-600 dark:text-primary-400 text-xs hover:underline"
			onclick={() => {
				isBulk = !isBulk;
				composerError = '';
				composerNotice = '';
			}}
		>
			{isBulk ? $t('Add one at a time') : $t('Add several at once')}
		</button>
	</div>

	{#if isBulk}
		<!-- A textarea because the input is a list, and lists arrive pasted. The seat
		     count stays a narrow field beside the button so it reads as "this many
		     seats each". -->
		<!-- Widths carry the `!` prefix throughout: flowbite merges its own `w-full`
		     with whatever is passed, and a plain `w-20` loses that merge — which is
		     what made the seat field span the panel and squeezed the label beside it
		     onto two lines. The textarea needs its wrapper sized as well as itself,
		     since the component renders one around the control. -->
		<div class="mt-2 space-y-2">
			<Textarea
				rows={3}
				divClass="!w-full"
				class="!w-full"
				placeholder={$t('colleague@example.com, another@example.com')}
				bind:value={bulkEmails}
				aria-label={$t('Email addresses')}
			/>
			<div class="flex items-center gap-2">
				<Input
					type="number"
					min="1"
					bind:value={newSeats}
					class="!w-20 shrink-0 text-center"
					aria-label={$t('Seats')}
				/>
				<span class="shrink-0 text-xs text-gray-500 dark:text-gray-400">{$t('seats each')}</span>
				<Button
					size="sm"
					color="alternative"
					class="ms-auto shrink-0"
					disabled={remaining <= 0}
					onclick={addMany}
				>
					<i class="fa-solid fa-plus me-2"></i>{$t('Add all')}
				</Button>
			</div>
			<p class="text-xs text-gray-500 dark:text-gray-400">
				{$t('Separate addresses with commas, spaces or new lines. People who already have seats are skipped.')}
			</p>
		</div>
	{:else}
		<!-- One row, and it stays one row: the seat count is a fixed narrow field, the
		     address takes the slack (`min-w-0` is what lets it shrink rather than
		     forcing a wrap), and the button holds its width. -->
		<div class="mt-2 flex items-center gap-2">
			<Input
				type="number"
				min="1"
				bind:value={newSeats}
				class="!w-20 shrink-0 text-center"
				aria-label={$t('Seats')}
			/>
			<Input
				type="email"
				placeholder={$t('colleague@example.com')}
				bind:value={newEmail}
				onkeydown={(event: KeyboardEvent) => event.key === 'Enter' && add()}
				aria-label={$t('Email address')}
				class="min-w-0 flex-1"
			/>
			<Button size="sm" color="alternative" class="shrink-0" disabled={remaining <= 0} onclick={add}>
				<i class="fa-solid fa-plus me-2"></i>{$t('Add')}
			</Button>
		</div>
	{/if}

	{#if composerError}
		<p class="mt-2 text-sm text-red-600 dark:text-red-400">{composerError}</p>
	{:else if composerNotice}
		<p class="mt-2 text-xs text-gray-600 dark:text-gray-300">{composerNotice}</p>
	{:else if remaining <= 0}
		<p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
			{$t('No seats left to assign. Reduce a block above first.')}
		</p>
	{/if}
</div>

{#if saveError}
	<p class="mt-3 text-sm text-red-600 dark:text-red-400">{saveError}</p>
{/if}

<div class="mt-4 flex flex-wrap items-center gap-2">
	<Button size="sm" disabled={!isDirty || saving} onclick={save}>
		{#if saving}<Spinner size="4" class="me-2" />{/if}
		{$t('Save assignments')}
	</Button>
	{#if isDirty}
		<Button size="sm" color="alternative" disabled={saving} onclick={reset}>{$t('Discard changes')}</Button>
	{/if}
</div>
